import { useRolodexEdit } from 'features/fde'
import { useKestrel } from 'features/kestrelViewer'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { MarkupLabelInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupLabelInput'
import { MarkupNotesInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupNotesInput'
import {
  InputGroup,
  InputHeader,
} from 'features/kestrelViewer/ui/popupMenu/shared/MarkupPopupContainer'
import { useEscapeKey } from 'features/kestrelViewer/ui/popupMenu/shared/useEscapeKey'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { KVCameraView, RolodexEntryPatchJson } from 'types'

export const CameraViewMenu: React.FC<{
  selectedCameraView: KVCameraView
}> = ({ selectedCameraView }) => {
  const { cameraViews, setSelectedMarkup } = useKestrelViewerContext()
  const { rolodexPatch } = useRolodexEdit()
  const {
    kestrelModel,
    cameraViews: kestrelCameraViews,
    setCameraViews,
  } = useKestrel()
  const labelInputRef = useRef<HTMLInputElement>(null)
  const notesInputRef = useRef<HTMLTextAreaElement>(null)
  const [localName, setLocalName] = useState(selectedCameraView.name)

  // Always get the current camera view data from the cameraViews array
  const currentCameraView = useMemo(() => {
    const found = cameraViews?.find((cv) => cv.id === selectedCameraView.id)
    if (!found) {
      return selectedCameraView
    }
    return found
  }, [cameraViews, selectedCameraView])

  useEffect(() => {
    setLocalName(currentCameraView.name)
  }, [currentCameraView.name])

  // Handle escape key to close popup
  useEscapeKey(() => setSelectedMarkup(undefined))

  // Focus on label input when popup opens
  useEffect(() => {
    if (labelInputRef.current) {
      labelInputRef.current.focus()
    }
  }, [])

  const handleTabKey = (
    e: React.KeyboardEvent,
    targetRef: React.RefObject<HTMLElement | null>
  ) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      if (targetRef.current) {
        targetRef.current.focus()
      }
    }
  }

  const setLabel = async () => {
    const trimmed = localName?.trim() || ''
    if (trimmed.length === 0) return
    if (!kestrelCameraViews || !kestrelModel?.id) return

    // Update local state
    const updatedCameraViews = kestrelCameraViews.map((cv) =>
      cv.id === currentCameraView.id ? { ...cv, name: trimmed } : cv
    )
    setCameraViews(updatedCameraViews)

    // Update rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: {
        cameraViews: updatedCameraViews.map((cv) => KVCameraView.toJSON(cv)),
      },
    }

    try {
      await rolodexPatch([patchData])
    } catch (error) {
      console.error('Failed to update camera view:', error)
    }
  }

  const setNotes = async (s: string) => {
    if (!kestrelCameraViews || !kestrelModel?.id) return

    // Update local state
    const updatedCameraViews = kestrelCameraViews.map((cv) =>
      cv.id === currentCameraView.id ? { ...cv, notes: s } : cv
    )
    setCameraViews(updatedCameraViews)

    // Update rolodex
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModel.id,
      data: {
        cameraViews: updatedCameraViews.map((cv) => KVCameraView.toJSON(cv)),
      },
    }

    try {
      await rolodexPatch([patchData])
    } catch (error) {
      console.error('Failed to update camera view:', error)
    }
  }

  return (
    <>
      <InputGroup>
        <InputHeader>Label</InputHeader>
        <MarkupLabelInput
          ref={labelInputRef}
          value={localName}
          onChange={setLocalName}
          placeholder='New Camera View'
          onEnter={setLabel}
          onTabKey={(e) => handleTabKey(e, notesInputRef)}
          saveOnBlur={true}
          requireNonEmpty={true}
          warningText='Label is required.'
        />
      </InputGroup>
      <InputGroup>
        <InputHeader>Notes</InputHeader>
        <MarkupNotesInput
          ref={notesInputRef}
          value={currentCameraView.notes}
          onChange={setNotes}
          placeholder='Add notes...'
          onTabKey={(e) => handleTabKey(e, labelInputRef)}
        />
      </InputGroup>
    </>
  )
}
